import cx from 'classnames'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from 'components/ModalSheet'
import { observer } from 'mobx-react'
import React from 'react'
import {
  CustomSelect,
  ItemSource,
  RenderSearchItemProps,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import { Form, FormGroupContainer, FormRow } from 'components/inputs'
import SearchResultItem from 'components/inputs/CustomSelect/SearchResultItem'
import Button from 'components/Button'
import styles from './RemoveClientsAsNonAdmin.sass'
import Icon from 'components/Icon'
import ErrorBlock from 'components/ErrorBlock'
import { Collaborator } from 'jobs/Job'
import RemoveClientsAsNonAdminViewModel from './RemoveClientsAsNonAdminViewModel'

interface RemoveClientsAsNonAdminProps {
  vm: RemoveClientsAsNonAdminViewModel
}

export default observer(function RemoveClientsAsNonAdminDialog({
  vm,
}: RemoveClientsAsNonAdminProps): JSX.Element {
  const itemSource = React.useMemo(
    () =>
      ItemSource.static<Collaborator>(vm.filteredCollaborators, (item, text) =>
        item.member.displayName.toLowerCase().includes(text.toLowerCase()),
      ),
    [vm.filteredCollaborators],
  )

  const selectProps = useCustomSelectStateFromItemSource({
    itemSource,
  })

  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.removeClient.pending ? undefined : vm.close}
    >
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <div className={styles.contentBlock}>
          <ModalSheetContentTitle compact align="left">
            <span className={styles.title}>Remove a supporting client</span>
          </ModalSheetContentTitle>

          <ModalSheetContentDescription compact align="left">
            <span className={styles.description}>
              The user will no longer be a collaborator on the job.
            </span>
          </ModalSheetContentDescription>
        </div>

        <ModalSheetContentBlock>
          <Form>
            <FormRow className={styles.row}>
              <FormGroupContainer
                model={vm}
                field="collaborator"
                label="Collaborator"
                className={styles.selectCollaboratorContainer}
                error={vm.validation.getError('collaborator')}
              >
                <CustomSelect
                  {...selectProps}
                  multi={false}
                  icon={<Icon name="dropdown" className={cx(styles.icon)} />}
                  itemToString={(i) => i.member.displayName}
                  placeholder="Select collaborator"
                  value={vm.collaborator}
                  onChange={vm.setCollaborator}
                  renderSearchItem={(props) => <RemoveSearchItem {...props} />}
                  data-test="remove-job-collaborator-as-non-admin-dropdown"
                />
              </FormGroupContainer>
            </FormRow>
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>

      {vm.removeClient.rejected && (
        <ErrorBlock tackOn="bottom" error={vm.removeClient.error} />
      )}

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button type="default" onClick={vm.close}>
            Close
          </Button>
          <Button
            type="danger"
            loading={vm.removeClient.pending}
            data-test="remove-job-collaborator-as-non-admin-submit-button"
            onClick={vm.removeClient}
            disabled={vm.disabled}
          >
            Remove
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface RemoveSearchItem extends RenderSearchItemProps<Collaborator> {}

const RemoveSearchItem = React.forwardRef(
  (
    { item, itemToString, ...itemProps }: RemoveSearchItem,
    ref: React.Ref<any>,
  ) => {
    return (
      <SearchResultItem
        className={cx(styles.searchResultItem)}
        ref={ref}
        {...itemProps}
        data-test={
          'remove-job-collaborator-as-non-admin-option-' +
          item.member.user.publicId?.toLowerCase()
        }
      >
        {item.member.displayName}
      </SearchResultItem>
    )
  },
)
